import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@ui/Button';
import { authService } from '@services/authService';
import { FloatingShapes } from '@ui/FloatingShapes';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', email: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.email) return;

    setStatus('sending');
    setErrorMessage('');

    try {
      await authService.requestPasswordReset(formData.username, formData.email);
      setStatus('sent');
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.response?.data?.message || '请求失败，请稍后重试。');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (status === 'sent') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700 relative overflow-hidden">
        <FloatingShapes />
        <div className="max-w-md w-full space-y-8 z-10 relative">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">邮件已发送</h2>
            <p className="text-blue-100 mb-4">
              密码重置邮件已发送到 <strong className="text-white">{formData.email}</strong>
            </p>
            <p className="text-sm text-blue-200 mb-6">
              请查收邮件并点击重置链接来设置新密码。链接有效期为1小时。
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              返回登录
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700 relative overflow-hidden">
      <FloatingShapes />
      <div className="max-w-md w-full space-y-8 z-10 relative">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-2">
            忘记密码
          </h2>
          <p className="text-blue-100">输入你的用户名和注册邮箱，我们将发送密码重置链接。</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{errorMessage}</p>
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                用户名
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="请输入你的用户名"
                disabled={status === 'sending'}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                邮箱地址
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="请输入注册时使用的邮箱"
                disabled={status === 'sending'}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={status === 'sending'}
            >
              {status === 'sending' ? '发送中...' : '发送重置链接'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <a
              href="/login"
              className="text-sm text-blue-200 hover:text-white"
            >
              返回登录
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
